import { object, string, bool, number, date } from 'yup';
import dayjs from 'dayjs';
import * as yup from 'yup';
import appConfig from '../../../appConfig';

const minAge = appConfig.regions.isUS ? 21 : 18;

const generateMaxBirthdayDate = () => dayjs().subtract(minAge, 'years').toDate();

const registrationSchema = object().shape({
	invite_code: string().nullable(),
	enable2fa: bool().nullable(),
	opted_out: bool(),
	name: string().required('Username is required'),
	first_name: string().required('First Name is required'),
	last_name: string().required('Last Name is required'),
	email: string().required('Email is required').email('Must be a valid email address'),
	password: string()
		.required('Password is required')
		.min(8, 'Password must have at least 8 characters')
		.matches(/[a-z]/, 'Password must contain at least 1 lowercase letter')
		.matches(/[A-Z]/, 'Password must contain at least 1 uppercase letter')
		.matches(/[0-9]/, 'Password must contain at least 1 number'),
	isManualAddress: yup.boolean(),
	country: string().required('Country is required'),
	phone: string()
		.required('Phone number is required')
		.matches(/^[0-9]+$/, 'Phone number must be numbers')
		.min(9, 'Mobile Number must be at least 9 characters')
		.when('country', {
			is: 'US',
			then: (schema) =>
				schema
					.required('Phone number is required')
					.matches(/^[0-9]+$/, 'Phone number must be numbers')
					.min(10, 'Phone number must be 10 digits')
					.max(10, 'Phone number must be 10 digits'),
		}),
	birth_date: date()
		.typeError('A valid date of Birth is required')
		.required('Date of Birth is required')
		.max(generateMaxBirthdayDate(), `You must be at least ${minAge} years old`),
	street_number: yup.string().when('isManualAddress', {
		is: true,
		then: (schema) => schema.required('Street number is required'),
	}),
	street_name: yup.string().when('isManualAddress', {
		is: true,
		then: (schema) => schema.required('Street name is required'),
	}),
	street_type: yup.string().when('isManualAddress', {
		is: true,
		then: (schema) => schema.required('Street type is required'),
	}),
	city: yup.string().when('isManualAddress', {
		is: true,
		then: (schema) => schema.required('City is required'),
	}),
	state: yup.string().when('isManualAddress', {
		is: true,
		then: (schema) => schema.required('State is required'),
	}),
	postcode: yup.string().when('isManualAddress', {
		is: true,
		then: (schema) => schema.required('Postcode is required for Australia'),
	}),
	ssn: string().when('country', {
		is: 'US',
		then: (schema) =>
			schema
				.required('SSN is required. It is only used for ID verification and is required by state legislation')
				.matches(/^[0-9]+$/, 'SSN can only be numbers')
				.min(4, 'Last four digits of SSN')
				.max(4, 'Last four digits of SSN'),
	}),
	set_deposit_limit: bool().when('country', {
		is: (country) => country !== 'US',
		then: (schema) =>
			schema
				.required('Please select whether you`d like to set a deposit limit or not')
				.typeError('Please select whether you`d like to set a deposit limit or not'),
	}),
	deposit_limit: object().when('set_deposit_limit', {
		is: true,
		then: (schema) =>
			schema.shape({
				limit: number()
					.typeError('Please insert a valid number')
					.required('Please select the amount for your deposit limit. Min $5.00')
					.min(5, 'Min amount is $5.00')
					.test('decimals', 'Please enter up to two decimals', (value) => {
						const strValue = value.toString();
						const decimalSplits = strValue.split('.');

						return (decimalSplits[1]?.length <= 2 && decimalSplits.length <= 2) || decimalSplits.length === 1;
					}),
				duration_type_id: number().required('Please select a valid duration'),
			}),
	}),
	address: yup.object().when('isManualAddress', {
		is: false,
		then: (schema) => {
			return schema.required('Address is required').shape({
				street_number: yup.string().required('Street number is required'),
				street_name: yup.string().required('Street name is required'),
				street_type: yup.string().required('Street type is required'),
				city: yup.string().required('City is required'),
				state: yup.string().required('State is required'),
				postcode: yup.string().required('Postcode is required for Australia'),
			});
		},
		otherwise: (schema) => schema.nullable(),
	}),
});

export const resetPasswordSchema = object().shape({
	currentPassword: string().required('Current Password is required'),
	confirmPassword: string().required('Confirm Password is required'),
	newPassword: string().required('New Password is required'),
});

export default registrationSchema;
